import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, CheckCircle, XCircle, User, Flame, HelpCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshUser, isGuest, addGuestPurchase } = useAuth();
  const { t, language } = useLanguage();
  const [status, setStatus] = useState('processing');
  const [purchaseInfo, setPurchaseInfo] = useState(null);
  const hasProcessed = useRef(false);
  const [countdown, setCountdown] = useState(3);
  const [showSupport, setShowSupport] = useState(false);
  const [manualSessionId, setManualSessionId] = useState('');
  const [claimError, setClaimError] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);

  // Read return path from localStorage (primary) or URL params (fallback)
  const getReturnPath = () => {
    const fromStorage = localStorage.getItem('erotika_payment_return');
    const fromUrl = searchParams.get('return_to');
    return fromStorage || fromUrl || '/select-gender';
  };

  // Process payment verification - runs only once
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = searchParams.get('session_id');
    const itemType = searchParams.get('type');
    const itemId = searchParams.get('item');
    const returnTo = getReturnPath();

    if (!sessionId) {
      localStorage.removeItem('erotika_payment_return');
      navigate(returnTo);
      return;
    }

    setPurchaseInfo({ type: itemType, id: itemId, returnTo });

    let attemptCount = 0;
    const maxAttempts = 20; // ~60s total (live Stripe 3DS can take 30-60s)

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/purchase/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Flag to trigger review modal when user returns to chat
          if (localStorage.getItem('velvet_review_done') !== 'true') {
            localStorage.setItem('erotika_review_after_purchase', 'true');
          }
          if (response.data.is_guest || isGuest) {
            addGuestPurchase(itemType, itemId);
          } else {
            await refreshUser();
          }
        } else if (response.data.status === 'expired') {
          setStatus('failed');
        } else if (attemptCount < maxAttempts) {
          attemptCount++;
          setTimeout(checkStatus, 3000); // 3s between polls
        } else {
          // Polling exhausted but the webhook may still complete later.
          // Fire a final reconcile attempt and show the failed screen with a recovery hint.
          try {
            const rec = await axios.post(`${API}/purchase/reconcile`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (rec.data && rec.data.count > 0) {
              setStatus('success');
              await refreshUser();
              return;
            }
          } catch (e) {
            console.warn('Reconcile after timeout failed:', e);
          }
          setStatus('failed');
        }
      } catch (error) {
        console.error('Check status error:', error);
        if (attemptCount < maxAttempts) {
          attemptCount++;
          setTimeout(checkStatus, 3000);
        } else {
          setStatus('failed');
        }
      }
    };

    checkStatus();
  }, []);

  // Auto-redirect countdown after success
  useEffect(() => {
    if (status !== 'success' || !purchaseInfo) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          localStorage.removeItem('erotika_payment_return');
          navigate(purchaseInfo.returnTo);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, purchaseInfo, navigate]);

  const handleContinue = () => {
    localStorage.removeItem('erotika_payment_return');
    navigate(purchaseInfo?.returnTo || '/select-gender');
  };

  const getSuccessMessage = () => {
    if (!purchaseInfo) return '';
    if (purchaseInfo.type === 'character') {
      return language === 'fr'
        ? 'Votre nouveau partenaire est maintenant disponible !'
        : 'Your new partner is now available!';
    } else if (purchaseInfo.type === 'level') {
      return language === 'fr'
        ? `Le niveau est maintenant débloqué !`
        : `The level is now unlocked!`;
    } else if (purchaseInfo.type === 'photos') {
      return language === 'fr'
        ? 'Les photos sont maintenant débloquées !'
        : 'Photos are now unlocked!';
    } else if (purchaseInfo.type === 'subscription') {
      return language === 'fr'
        ? 'Votre abonnement est maintenant actif !'
        : 'Your subscription is now active!';
    }
    return '';
  };

  const getIcon = () => {
    if (!purchaseInfo) return <CheckCircle className="text-green-400" size={40} />;
    if (purchaseInfo.type === 'character') {
      return <User className="text-purple-light" size={40} />;
    } else if (purchaseInfo.type === 'level') {
      return <Flame className="text-purple-light fill-current" size={40} />;
    }
    return <CheckCircle className="text-green-400" size={40} />;
  };

  return (
    <div className="min-h-screen bg-velvet-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {status === 'processing' && (
          <div className="animate-slideUp">
            <div className="w-20 h-20 bg-purple-main/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="text-purple-light animate-spin" size={40} />
            </div>
            <h1 className="font-heading text-3xl text-velvet_text-primary mb-4">
              {t('payment_processing')}
            </h1>
            <p className="text-velvet_text-secondary">
              {language === 'fr'
                ? 'Veuillez patienter pendant la vérification...'
                : 'Please wait while we verify your payment...'
              }
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-slideUp">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-400" size={40} />
            </div>
            <div className="w-16 h-16 bg-purple-main/20 rounded-full flex items-center justify-center mx-auto mb-4">
              {getIcon()}
            </div>
            <h1 className="font-heading text-3xl text-velvet_text-primary mb-4">
              {t('payment_success')}
            </h1>
            <p className="text-velvet_text-secondary mb-4">
              {getSuccessMessage()}
            </p>
            <p className="text-velvet_text-secondary text-sm mb-6">
              {language === 'fr'
                ? `Redirection automatique dans ${countdown}s...`
                : `Auto-redirect in ${countdown}s...`
              }
            </p>
            <Button
              onClick={handleContinue}
              className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-8 py-6"
              data-testid="continue-button"
            >
              {language === 'fr' ? 'Continuer maintenant' : 'Continue now'}
            </Button>
          </div>
        )}

        {status === 'failed' && (
          <div className="animate-slideUp">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="text-red-400" size={40} />
            </div>
            <h1 className="font-heading text-3xl text-velvet_text-primary mb-4">
              {t('payment_failed')}
            </h1>
            <p className="text-velvet_text-secondary mb-4">
              {language === 'fr'
                ? "Le paiement n'a pas pu être confirmé dans le délai imparti."
                : 'The payment could not be confirmed within the expected time.'}
            </p>
            <p className="text-velvet_text-secondary text-sm mb-8">
              {language === 'fr'
                ? "Si ta carte a été débitée, clique ci-dessous pour re-synchroniser. Ton achat sera débloqué automatiquement."
                : 'If your card was charged, tap below to re-sync. Your purchase will be unlocked automatically.'}
            </p>
            <div className="flex flex-col gap-3 items-center">
              <Button
                onClick={async () => {
                  setStatus('processing');
                  try {
                    const rec = await axios.post(`${API}/purchase/reconcile`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (rec.data && rec.data.count > 0) {
                      await refreshUser();
                      setStatus('success');
                    } else {
                      setStatus('failed');
                    }
                  } catch (e) {
                    console.error('Manual reconcile failed:', e);
                    setStatus('failed');
                  }
                }}
                className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-8 py-6"
                data-testid="reconcile-payment-button"
              >
                {language === 'fr' ? 'Re-synchroniser mon paiement' : 'Re-sync my payment'}
              </Button>
              <Button
                onClick={handleContinue}
                variant="outline"
                className="rounded-full px-8 py-6"
                data-testid="back-button"
              >
                {t('back')}
              </Button>

              <button
                type="button"
                onClick={() => setShowSupport(!showSupport)}
                className="flex items-center gap-1 text-xs text-velvet_text-secondary/70 hover:text-velvet_text-primary transition mt-4"
                data-testid="toggle-support-button"
              >
                <HelpCircle size={14} />
                {language === 'fr'
                  ? "Toujours un souci ? Saisis ton ID Stripe"
                  : "Still stuck? Enter your Stripe session ID"}
              </button>

              {showSupport && (
                <div className="w-full mt-4 p-4 bg-black/5 rounded-xl text-left">
                  <p className="text-xs text-velvet_text-secondary mb-2">
                    {language === 'fr'
                      ? "Copie-colle l'ID de session Stripe (format cs_live_...) — tu le trouves dans l'email de reçu Stripe ou sur dashboard.stripe.com/payments."
                      : "Paste the Stripe session ID (format cs_live_...) — from your Stripe receipt email or dashboard.stripe.com/payments."}
                  </p>
                  <Input
                    value={manualSessionId}
                    onChange={(e) => { setManualSessionId(e.target.value); setClaimError(''); }}
                    placeholder="cs_live_..."
                    className="mb-2"
                    data-testid="manual-session-input"
                  />
                  {claimError && (
                    <p className="text-xs text-red-500 mb-2" data-testid="claim-error">{claimError}</p>
                  )}
                  <Button
                    onClick={async () => {
                      const sid = manualSessionId.trim();
                      if (!sid.startsWith('cs_live_') && !sid.startsWith('cs_test_')) {
                        setClaimError(language === 'fr'
                          ? "L'ID doit commencer par cs_live_ ou cs_test_."
                          : "ID must start with cs_live_ or cs_test_.");
                        return;
                      }
                      setClaimLoading(true);
                      setClaimError('');
                      try {
                        const resp = await axios.post(
                          `${API}/purchase/claim-by-session`,
                          { session_id: sid },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (resp.data && resp.data.claimed) {
                          await refreshUser();
                          setStatus('success');
                        } else {
                          setClaimError(resp.data?.message || (language === 'fr'
                            ? 'Paiement pas encore confirmé côté Stripe.'
                            : 'Payment not yet confirmed on Stripe.'));
                        }
                      } catch (e) {
                        setClaimError(e.response?.data?.detail || (language === 'fr'
                          ? 'Erreur lors de la vérification.'
                          : 'Verification error.'));
                      } finally {
                        setClaimLoading(false);
                      }
                    }}
                    disabled={claimLoading || !manualSessionId.trim()}
                    className="w-full bg-terracotta hover:bg-terracotta/90 text-white rounded-full"
                    data-testid="claim-by-session-button"
                  >
                    {claimLoading
                      ? (language === 'fr' ? 'Vérification...' : 'Verifying...')
                      : (language === 'fr' ? 'Vérifier et débloquer' : 'Verify and unlock')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
